import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { AllSettings, ClaudeStatus, NextUp, Place, PlanResponse, RoutineDTO, Source, TaskDTO } from '@tempo/core';
import { api } from '../api';
import { fromISODate, setZone } from './format';
import { toast } from './toast';

/** What this Tempo has. A friend's Tempo (scripts/users.mjs) has no School Manager and reaches Claude by API key only. */
export interface Features {
  schoolManager: boolean;
  claudeCli: boolean;
}
export interface AuthStatus {
  setUp: boolean;
  authenticated: boolean;
  setupCodeRequired?: boolean;
  features?: Features;
}
export const authQuery = { queryKey: ['auth'], queryFn: () => api.get<AuthStatus>('/auth/status') };

export function useFeatures(): Features {
  const q = useQuery({ ...authQuery, staleTime: Infinity });
  return q.data?.features ?? { schoolManager: true, claudeCli: true };
}

export function useSettings() {
  const q = useQuery({ queryKey: ['settings'], queryFn: () => api.get<AllSettings>('/settings'), staleTime: 60_000 });
  if (q.data) setZone(q.data.planner.timezone);
  return q;
}

/** Whether live explanations can run (Claude signed in on the PC / relay online / API key set). */
export function useClaudeStatus(enabled = true) {
  return useQuery({
    queryKey: ['claude-status'],
    queryFn: () => api.get<ClaudeStatus>('/assistant/claude'),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

export function usePlan(date: string, days = 1) {
  return useQuery({
    queryKey: ['plan', date, days],
    queryFn: () => {
      const from = fromISODate(date).startOf('day');
      return api.get<PlanResponse>(`/plan?from=${encodeURIComponent(from.toISO()!)}&to=${encodeURIComponent(from.plus({ days }).toISO()!)}`);
    },
    refetchInterval: 5 * 60_000,
  });
}

export const useNextUp = () => useQuery({ queryKey: ['next'], queryFn: () => api.get<NextUp>('/next'), refetchInterval: 60_000 });
export const usePlaces = () => useQuery({ queryKey: ['places'], queryFn: () => api.get<Place[]>('/places') });
export const useSources = () => useQuery({ queryKey: ['sources'], queryFn: () => api.get<Source[]>('/sources') });
export const useRoutines = () => useQuery({ queryKey: ['routines'], queryFn: () => api.get<RoutineDTO[]>('/routines') });
export const useTasks = (includeDone = false) =>
  useQuery({ queryKey: ['tasks', includeDone], queryFn: () => api.get<TaskDTO[]>(`/tasks?includeDone=${includeDone}`) });

/** A mutation that refreshes everything afterwards (the plan may have moved) and toasts errors. */
export function useAction<TArgs = void, TResult = unknown>(fn: (args: TArgs) => Promise<TResult>, success?: string | ((r: TResult) => string)) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      qc.invalidateQueries();
      if (success) toast(typeof success === 'function' ? success(r) : success);
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });
}

/** Re-render every `ms` (for countdowns / the now-line). */
export function useNow(ms = 30_000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function useMediaQuery(query: string) {
  const [match, setMatch] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setMatch(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return match;
}

/** Live updates: the server pushes an event whenever the plan changes. */
export function usePlanStream(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    const es = new EventSource('/api/v1/stream');
    es.addEventListener('plan', () => qc.invalidateQueries());
    return () => es.close();
  }, [enabled, qc]);
}

const LOC_KEY = 'tempo.shareLocation';
export const locationSharing = {
  get: () => {
    try {
      return localStorage.getItem(LOC_KEY) === '1';
    } catch {
      return false;
    }
  },
  set: (on: boolean) => {
    try {
      localStorage.setItem(LOC_KEY, on ? '1' : '0');
    } catch {
      /* private mode */
    }
    window.dispatchEvent(new Event('tempo:location-pref'));
  },
};

export function currentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Location is not available on this device'));
    navigator.geolocation.getCurrentPosition(resolve, (e) => reject(new Error(e.code === 1 ? 'Location permission was denied' : 'Could not get your location')), {
      enableHighAccuracy: true,
      timeout: 15_000,
      maximumAge: 60_000,
    });
  });
}

export async function sendLocation() {
  const pos = await currentPosition();
  return api.post<NextUp>('/location', { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, source: 'app' });
}

/** While the app is open and sharing is on, report location every 5 minutes (used for live leave-by times). */
export function useLocationReporting() {
  const qc = useQueryClient();
  const [on, setOn] = useState(locationSharing.get());
  useEffect(() => {
    const update = () => setOn(locationSharing.get());
    window.addEventListener('tempo:location-pref', update);
    return () => window.removeEventListener('tempo:location-pref', update);
  }, []);
  useEffect(() => {
    if (!on) return;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      sendLocation()
        .then((next) => qc.setQueryData(['next'], next))
        .catch(() => undefined);
    };
    tick();
    const t = setInterval(tick, 5 * 60_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [on, qc]);
}
